import DOMUtils from "@utils/dom.utils.ts";
import i18n from "@plugins/i18n.ts";
import { isEmpty } from "@utils/var.check.ts";
import { MentionableUser } from "./types.ts";

interface MentionDropdownTextItem {
  type: "cardtext";
  text: string;
  name?: string;
  classes: string[];
}

interface MentionDropdownItem {
  type: "cardmenuitem";
  value: {
    label: string;
    value: string;
  };
  text: string;
  items: {
    type: "cardcontainer";
    direction: "vertical";
    items: MentionDropdownTextItem[];
  }[];
}

interface MentionSelectionItem {
  label?: string;
  value?: string | null;
}

function getMentionRoleLabel(user: MentionableUser): string | null {
  if (!user.isTeacher) {
    return null;
  }

  return user.roleLabel ?? String(i18n.t("app_side_nav_role_name_teacher"));
}

function getTeacherAggregateLabel(users: MentionableUser[]): string {
  const aggregateLabel = users.find((user) => !isEmpty(user.aggregateLabel))?.aggregateLabel;

  return aggregateLabel ?? String(i18n.t("announcement_receiver_all_teachers_label"));
}

function getStudentAggregateLabel(users: MentionableUser[]): string {
  const aggregateLabel = users.find((user) => !isEmpty(user.aggregateLabel))?.aggregateLabel;

  return aggregateLabel ?? String(i18n.t("announcement_receiver_all_students_label"));
}

function getMentionSelectionValue(item: MentionSelectionItem): string {
  if (typeof item.value === "string") {
    return item.value;
  }
  return "";
}

function getMentionSelectionLabel(item: MentionSelectionItem): string {
  if (typeof item.label === "string") {
    return item.label;
  }
  return "";
}

function toMentionableUsers(response: unknown): MentionableUser[] {
  return Array.isArray(response) ? response.filter((user) => user !== null) : [];
}

export default function (editor) {
  const SLS_MENTION_SELECTOR = "span.sls-mention";

  const richTextEditor = editor.options.get("richTextEditor");

  /* These are "local" caches of the data returned from the fake server */

  (function init() {
    // Initialize mentions with the configuration
    let mentionedTeachers: MentionableUser[] = [];
    let mentionedStudents: MentionableUser[] = [];
    let mentionedAppFeatures: MentionableUser[] = [];
    let mentionableTeachers: MentionableUser[] = [];
    let mentionableStudents: MentionableUser[] = [];
    let mentionableAppFeatures: MentionableUser[] = [];
    let mentionableList: MentionableUser[] = [];
    const mentionConfig = richTextEditor.config?.plugins?.customMention?.mentionConfig;

    function rebuildMentionableList() {
      const mentionedStudentUuids = mentionedStudents.map((user) => user.userUuid);
      const mentionedTeacherUuids = mentionedTeachers.map((user) => user.userUuid);
      const mentionedAppFeatureUuids = mentionedAppFeatures.map((user) => user.userUuid);

      mentionableList = [
        ...mentionableAppFeatures
          .filter((user) => !mentionedAppFeatureUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
        ...mentionableStudents
          .filter((user) => !mentionedStudentUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
        ...mentionableTeachers
          .filter((user) => !mentionedTeacherUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ];
    }

    async function populateMentionableList() {
      const mentionedStudentUuids = mentionedStudents.map((user) => user.userUuid);
      const mentionedTeacherUuids = mentionedTeachers.map((user) => user.userUuid);
      const mentionedAppFeatureUuids = mentionedAppFeatures.map((user) => user.userUuid);

      const promiseArray = [
        mentionConfig.getMentionableStudentList(mentionConfig.includeBuddyListMentions),
        mentionConfig.getMentionableTeacherList(),
      ];
      const hasMentionableAppFeature =
        typeof mentionConfig.getMentionableAppFeatureList === "function";
      if (hasMentionableAppFeature) {
        promiseArray.push(mentionConfig.getMentionableAppFeatureList());
      }

      const responses = await Promise.all(promiseArray);
      mentionableStudents = toMentionableUsers(responses[0]);
      mentionableTeachers = toMentionableUsers(responses[1]);
      if (hasMentionableAppFeature) {
        mentionableAppFeatures = toMentionableUsers(responses[2]);
      }

      mentionableList = [
        ...mentionableAppFeatures
          .filter((user) => !mentionedAppFeatureUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
        ...mentionableStudents
          .filter((user) => !mentionedStudentUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
        ...mentionableTeachers
          .filter((user) => !mentionedTeacherUuids.includes(user.userUuid))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ];
    }

    async function mentions_fetch(query: string) {
      await populateMentionableList();
      let filtered = [...mentionableList];

      const hasUnmentionedStudents: boolean = filtered.some(
        (user) => user.isTeacher !== true && !user.isAppFeature,
      );
      const hasUnmentionedTeachers: boolean = filtered.some(
        (user) => user.isTeacher === true && !user.isAppFeature,
      );

      /* `query` is the text the user typed after the '@' */
      if (query) {
        filtered = mentionableList.filter((user) =>
          user.name.toLowerCase().includes(query.toLowerCase()),
        );
      }

      const results = filtered.map((user) => {
        return getMentionDropdown(user);
      });

      // All Students
      const allStudentsOption: MentionableUser = {
        userUuid: "allStudents",
        name: getStudentAggregateLabel(mentionableStudents),
      };

      // show if there are mentionable students + (no query or query is match)
      const showAllStudentsOption =
        hasUnmentionedStudents &&
        (isEmpty(query) || allStudentsOption.name.toLowerCase().includes(query.toLowerCase()));

      if (showAllStudentsOption) {
        results.push(getMentionDropdown(allStudentsOption));
      }

      // All Teachers
      const allTeachersOption: MentionableUser = {
        userUuid: "allTeachers",
        name: getTeacherAggregateLabel(mentionableTeachers),
      };

      // show if there are mentionable teachers + (no query or query is match)
      const showAllTeachersOption =
        hasUnmentionedTeachers &&
        (isEmpty(query) || allTeachersOption.name.toLowerCase().includes(query.toLowerCase()));

      if (showAllTeachersOption) {
        results.push(getMentionDropdown(allTeachersOption));
      }

      return results;
    }

    function getMentionDropdown(user: MentionableUser): MentionDropdownItem {
      const roleLabel = getMentionRoleLabel(user);
      let mentionDropdownTextItems: MentionDropdownTextItem[];
      if (roleLabel) {
        mentionDropdownTextItems = [
          {
            type: "cardtext",
            text: user.name,
            name: "char_name",
            classes: ["sls-mention-name"],
          },
          {
            type: "cardtext",
            text: roleLabel,
            classes: ["sls-mention-role"],
          },
        ];
      } else {
        mentionDropdownTextItems = [
          {
            type: "cardtext",
            text: user.name,
            name: "char_name",
            classes: ["sls-mention-name"],
          },
        ];
      }

      return {
        type: "cardmenuitem",
        value: {
          label: user.name,
          value: user.userUuid,
        },
        text: user.name,
        items: [
          {
            type: "cardcontainer",
            direction: "vertical",
            items: mentionDropdownTextItems,
          },
        ],
      };
    }

    function mentions_select(autocompleteApi, rng, item: MentionSelectionItem) {
      let uuids: string[];
      const mentionValue = getMentionSelectionValue(item);
      const mentionLabel = getMentionSelectionLabel(item);

      if (mentionValue === "allStudents") {
        uuids = mentionableStudents.map((user) => user.userUuid);
      } else if (mentionValue === "allTeachers") {
        uuids = mentionableTeachers.map((user) => user.userUuid);
      } else {
        uuids = [mentionValue];
      }
      const users = mentionableList.filter((user) => uuids.includes(user.userUuid));
      const selectedUser = users[0];

      if (!selectedUser) {
        autocompleteApi.hide();
        return;
      }

      editor?.selection.setRng(rng);
      createInlineMention(
        mentionLabel,
        selectedUser.isTeacher === true,
        mentionValue,
        selectedUser.isAppFeature === true,
      );
      autocompleteApi.hide();
    }

    richTextEditor.plugins["customMentions"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      processOnChange,
    };
    editor.on("init", function () {
      // Add mentions plugin to TinyMCE editor instance
      editor.ui.registry.addAutocompleter("mentions", {
        trigger: "@", // Trigger character
        minChars: 0, // Minimum characters to start searching
        highlightOn: ["char_name"], // highlight typed characters
        fetch: mentions_fetch, // Handles fetching mentionable users
        onAction: mentions_select, // Handles what happens when a mention is selected
      });
    });

    function processOnChange(editor) {
      mentionedAppFeatures = [];
      mentionedTeachers = [];
      mentionedStudents = [];
      const elements = editor.bodyElement.querySelectorAll("[data-mention-uuids]");

      const promiseArray = [
        mentionConfig.getMentionableStudentList(mentionConfig.includeBuddyListMentions),
        mentionConfig.getMentionableTeacherList(),
      ];
      const hasMentionableAppFeature =
        typeof mentionConfig.getMentionableAppFeatureList === "function";
      if (hasMentionableAppFeature) {
        promiseArray.push(mentionConfig.getMentionableAppFeatureList());
      }
      Promise.all(promiseArray).then((responses) => {
        mentionableStudents = toMentionableUsers(responses[0]);
        mentionableTeachers = toMentionableUsers(responses[1]);
        if (hasMentionableAppFeature) {
          mentionableAppFeatures = toMentionableUsers(responses[2]);
        }

        elements.forEach((el) => {
          let mentionableList: MentionableUser[] = [];
          let uuids = el.getAttribute("data-mention-uuids");
          const isTeacher = el.getAttribute("data-teacher") === "true"; // Convert to boolean
          const isAppFeature = el.getAttribute("data-app-feature") === "true"; // Convert to boolean

          if (uuids === "allStudents") {
            uuids = mentionableStudents.map((user) => user.userUuid);
          } else if (uuids === "allTeachers") {
            uuids = mentionableTeachers.map((user) => user.userUuid);
          } else if (uuids) {
            uuids = uuids.split(",");
          }
          if (uuids && uuids.length > 0) {
            if (isTeacher) {
              mentionableList = mentionableTeachers.filter((user) => uuids.includes(user.userUuid));
              mentionedTeachers = mentionedTeachers.concat(mentionableList);
            } else if (isAppFeature) {
              mentionableList = mentionableAppFeatures.filter((user) =>
                uuids.includes(user.userUuid),
              );
              mentionedAppFeatures = mentionedAppFeatures.concat(mentionableList);
            } else {
              mentionableList = mentionableStudents.filter((user) => uuids.includes(user.userUuid));
              mentionedStudents = mentionedStudents.concat(mentionableList);
            }
          }
        });
        rebuildMentionableList();
        if (editor.hasFocus()) {
          editor.execCommand(
            "mceAutocompleterReload",
            false,
            {
              fetchOptions: {},
            },
            {
              skipFocus: true,
            },
          );
        }

        richTextEditor.handleMention({
          mentionedAppFeatures: [...new Set(mentionedAppFeatures)],
          mentionedTeachers: [...new Set(mentionedTeachers)],
          mentionedStudents: [...new Set(mentionedStudents)],
        });
      });
    }

    function renderInEditor() {
      renderInlineMention();
      processOnChange(editor);
    }

    function createInlineMention(label, isTeacher, mentionIds, isAppFeature = false) {
      const span = document.createElement("span");
      span.className = "sls-mention";
      span.setAttribute("contenteditable", "false");
      const userName = "@" + label;

      span.setAttribute("data-app-feature", String(isAppFeature));
      span.setAttribute("data-teacher", String(isTeacher));
      span.setAttribute("data-mention-uuids", mentionIds);
      span.setAttribute("data-name", userName);

      editor?.insertContent(span.outerHTML + "&nbsp;");
    }

    function renderInlineMention() {
      // select all span with class "sls-mention"
      const slsMentions = DOMUtils.elOf(editor.getBody(), SLS_MENTION_SELECTOR);

      slsMentions.forEach((slsMention) => {
        slsMention.setAttribute("contenteditable", "false");
      });
    }

    function cleanUp(content) {
      const container = document.createElement("div");
      container.innerHTML = content;

      DOMUtils.elOf(container, SLS_MENTION_SELECTOR).forEach((element) => {
        element.removeAttribute("contenteditable");
      });

      return container.innerHTML;
    }

    function containPluginElements() {
      const slsMentions = DOMUtils.elOf(editor.getBody(), SLS_MENTION_SELECTOR);

      return !isEmpty(slsMentions);
    }
  })();
}
